import Image from 'next/image';
import React, { useState } from 'react';
import { ClubMember } from '../../types';

import {
    faBorderAll,
    faCalendarCheck,
    faComments,
    faEdit,
    faEnvelope,
    faFileInvoiceDollar,
    faFileSignature,
    faFolder,
    faHistory,
    faHorse,
    faIdCard,
    faPaperPlane,
    faPause,
    faPhone,
    faPlay,
    faPlus,
    faPrint,
    faRotateLeft,
    faReceipt,
    faTag,
    faTicketAlt,
    faTimes,
    faTrash,
    faUserPlus,
    faUserXmark
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useModalManager from '@hooks/useModalManager';
import { Badge, Button, InlineTabBar, Tooltip } from '@ui';
import { MessageComponent } from 'components/ClubMemberManagement/MessageComponent';
import Loader from 'components/Loader';
import { isModuleVisible } from 'helpers/clubModules';
import { useExperimental } from 'hooks/useExperimental';
import { useVisibleModules } from 'hooks/useVisibleModules';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useToastStore } from '~/components/ToastManager/useToasts';
import useCheckPermissions from '~/hooks/useCheckPermissions';
import { useConfirmedCallback } from '~/hooks/useConfirmedCallback';
import { useDownload } from '../../../../hooks';
import { useApi } from '../../../../hooks/useApi';
import LetterComponent from '../../LetterComponent';
import { ClubMemberForm } from '../ClubMemberForm';
import { ServiceHoursComponent } from '../ServiceHoursComponent';
import SuspendClubMemberForm from '../SuspendClubMemberForm';
import CancelClubMemberForm from '../CancelClubMemberForm';
import ClubMemberAccountForm from '../ClubMemberAccountForm';
import { ClubMemberFileManager } from '../../ClubMemberFiles';
import ClubMemberAllocationsList from './ClubMemberAllocationsList';
import ClubMemberApplications from './ClubMemberApplications';
import ClubMemberBookingsList from './ClubMemberBookingsList';
import ClubMemberCategoryHistoryTimeline from './ClubMemberCategoryHistoryTimeline';
import ClubMemberMessagesList from './ClubMemberMessagesList';
import ClubMemberOpenItems from './ClubMemberOpenItems';
import ClubMemberPropertiesTab from './ClubMemberPropertiesTab';
import ClubMemberVoucherList from './ClubMemberVoucherList';
import ContributionsPreview from './ContributionsPreview';
import { LetterList } from './LetterList';

// Loaded on demand: the equestrian panel is one experimental module out of nineteen, and
// every other club would otherwise carry it in the member preview bundle for nothing.
const MemberHorses = dynamic(() => import('components/Equestrian/MemberHorses'));

interface Props {
    listRef?: React.MutableRefObject<any>;
    clubSlug: string;
    initialTab?: string;
}

interface PropsWithId extends Props {
    clubMemberId?: number;
    clubMember?: never;
}

interface PropsWithMember extends Props {
    clubMember?: ClubMember;
    clubMemberId?: never;
}

export const ClubMemberPreview = ({
    clubMemberId,
    clubMember: _clubMember,
    listRef,
    clubSlug,
    initialTab
}: PropsWithId | PropsWithMember) => {
    const { t } = useTranslation('member-management');
    // The horse tab keeps its wording in the module's own namespace, so renaming the
    // module later does not mean hunting for a stray copy in the member management texts.
    const { t: tEquestrian } = useTranslation('equestrian');
    const resolvedClubMemberId = clubMemberId ?? _clubMember?.id;
    const { addModal, removeModal } = useModalManager();
    const api = useApi();
    const { addToast } = useToastStore();
    const download = useDownload();
    const [activeTab, setActiveTab] = useState(initialTab ?? 'eigenschaften');

    const {
        data: clubMemberData,
        mutate,
        isLoading
    } = useSWR(resolvedClubMemberId ? `club-member/${resolvedClubMemberId}` : null, null, {
        fallbackData: _clubMember ? { data: _clubMember } : undefined
    });
    const { data: propertyData } = useSWR(clubSlug ? `club-member/${clubSlug}/property` : null);

    // The horse tab follows the same two gates as the sidebar entry: the club has to have
    // the module switched on and the user has to carry the experimental flag. The
    // permission check is deliberately not here but inside the panel, where it costs a
    // request only once the tab is actually opened.
    const isEquestrianEnabled = useExperimental().includes('equestrian_v1');
    const { visible: visibleModules } = useVisibleModules(clubSlug, isEquestrianEnabled);
    const showHorses = isEquestrianEnabled && isModuleVisible(visibleModules, 'horses');

    const clubMember = clubMemberData?.data ?? _clubMember ?? {};
    const previewClubMember =
        clubMember?.id || !resolvedClubMemberId ? clubMember : { ...clubMember, id: resolvedClubMemberId };

    const properties = propertyData?.data?.map((prop) => ({
        label: prop.title,
        type: prop.type,
        value: clubMember?.[`p_${prop.id}`]
    }));

    const scheduledAssignments = (() => {
        const raw = Array.isArray(clubMember?.scheduled_category_assignments)
            ? clubMember.scheduled_category_assignments
            : Object.values(clubMember?.scheduled_category_assignments ?? {});
        return raw
            .filter((a) => a?.scheduled_at)
            .sort((a, b) => moment.parseZone(a.scheduled_at).valueOf() - moment.parseZone(b.scheduled_at).valueOf());
    })();

    const formatScheduledAt = (scheduledAt?: string | null) =>
        scheduledAt ? moment.parseZone(scheduledAt).format('L LT') : '';

    const removeScheduledCategoryAssignment = useConfirmedCallback((id?: number) => {
        if (!id) return;
        return api({
            url: `club-member/${clubSlug}/category/scheduled/${id}`,
            method: 'DELETE',
            onSuccess: () => mutate()
        });
    });

    const openLetterFormHandler = () =>
        addModal({
            title: t('clubMember.preview.modals.letterTo', { name: clubMember.name }),
            content: <LetterComponent members={[clubMember.id]} />,
            show: true,
            size: 70
        });

    const openMessageComponentHandler = () =>
        addModal({
            title: t('clubMember.preview.modals.messageTo', { name: clubMember.name }),
            content: (id) => (
                <MessageComponent
                    members={[String(clubMember.id)]}
                    clubSlug={clubSlug}
                    callback={() => removeModal(id)}
                />
            ),
            show: true,
            size: 60
        });

    const openAccountFormHandler = () =>
        addModal({
            title: t('clubMember.preview.modals.createAccountFor', { name: clubMember.name }),
            content: (id) => (
                <ClubMemberAccountForm
                    clubSlug={clubSlug}
                    member={clubMember}
                    members={[String(clubMember.id)]}
                    callback={() => {
                        removeModal(id);
                        mutate();
                    }}
                    onUpdated={(updated) => listRef?.current?.update(updated.id, updated)}
                />
            ),
            show: true,
            size: 60
        });

    const openClubMemberFormHandler = () =>
        addModal({
            title: t('clubMember.preview.modals.editingOf', { name: clubMember.name }),
            content: (id) => (
                <ClubMemberForm
                    clubSlug={clubSlug}
                    member={clubMember}
                    callback={(newClubMember) => {
                        listRef.current?.update(newClubMember.id, newClubMember);
                        removeModal(id);
                        mutate();
                    }}
                />
            ),
            show: true,
            size: 60
        });

    // opens the suspension dialog — open ended or for a defined period
    const openSuspendFormHandler = () =>
        addModal({
            title: t('clubMember.list.modals.suspendMemberTitle', { name: clubMember.name }),
            content: (id) => (
                <SuspendClubMemberForm
                    member={clubMember}
                    onSuccess={(newClubMember) => {
                        listRef.current?.update(newClubMember.id, newClubMember);
                        listRef.current?.reload();
                    }}
                    callback={() => {
                        removeModal(id);
                        mutate();
                    }}
                />
            ),
            show: true
        });

    const openCancelFormHandler = () =>
        addModal({
            title: t('clubMember.exit.modalTitle', { name: clubMember.name }),
            content: (id) => (
                <CancelClubMemberForm
                    member={clubMember}
                    onSuccess={(newClubMember) => listRef.current?.update(newClubMember.id, newClubMember)}
                    callback={() => {
                        removeModal(id);
                        mutate();
                    }}
                />
            ),
            show: true
        });

    const revertCancellationHandler = useConfirmedCallback(
        () =>
            api({
                url: `club-member/${clubMember.id}/exit`,
                method: 'DELETE',
                onSuccess: ({ data, skipped_lockers }) => {
                    if (skipped_lockers?.length) {
                        addToast({
                            type: 'alert',
                            title: t('clubMember.exit.skippedLockers', { numbers: skipped_lockers.join(', ') })
                        });
                    }
                    listRef.current?.update(data.id, data);
                    mutate();
                }
            }),
        { message: () => t('clubMember.exit.revertConfirm', { name: clubMember?.name }) }
    );

    // lifts the running suspension, or reactivates a legacy deactivated member
    const liftSuspensionHandler = useConfirmedCallback(() =>
        api({
            url: clubMember.suspension
                ? `club-member/${clubMember.id}/suspension/${clubMember.suspension.id}/lift`
                : `club-member/${clubMember.id}/activate`,
            method: clubMember.suspension ? 'PUT' : 'GET',
            onSuccess: ({ data }) => {
                listRef.current?.update(data.id, data);
                listRef.current?.reload();
                mutate();
            }
        })
    );

    const handleDownloadMemberCardPrintTemplate = () => {
        void api({
            url: `club-member/${clubMember.id}/card-print`,
            method: 'GET',
            responseType: 'blob',
            onSuccess: (data) => download(data, `${clubMember.name}_card.pdf`)
        });
    };

    // Drives the visibility of the receivables tab; the endpoints authorize on their own.
    const { hasAccess: canViewInvoices } = useCheckPermissions({ clubSlug, permission: 'user_invoice.view' });
    const { hasAccess: canViewFiles } = useCheckPermissions({ clubSlug, permission: 'member_management.file' });
    const { hasAccess: canViewApplications } = useCheckPermissions({
        clubSlug,
        permission: 'member_management.application.view'
    });

    const tabs = [
        {
            key: 'eigenschaften',
            label: t('clubMember.preview.tabs.properties'),
            icon: <FontAwesomeIcon icon={faIdCard} />
        },
        {
            key: 'pferde',
            label: tEquestrian('nav.title'),
            icon: <FontAwesomeIcon icon={faHorse} />,
            show: showHorses
        },
        {
            key: 'rechnungsvorschau',
            label: t('clubMember.preview.tabs.contributionsPreview'),
            icon: <FontAwesomeIcon icon={faReceipt} />
        },
        {
            key: 'kategorie-historie',
            label: t('clubMember.preview.tabs.categoryHistory'),
            icon: <FontAwesomeIcon icon={faHistory} />
        },
        {
            key: 'geplante-aenderungen',
            label: t('clubMember.preview.tabs.scheduledChanges'),
            icon: <FontAwesomeIcon icon={faCalendarCheck} />,
            show: scheduledAssignments.length > 0
        },
        {
            key: 'rechnungen',
            label: t('clubMember.preview.tabs.invoices'),
            icon: <FontAwesomeIcon icon={faFileInvoiceDollar} />,
            show: canViewInvoices
        },
        { key: 'bookings', label: 'Buchungen', icon: <FontAwesomeIcon icon={faTicketAlt} /> },
        { key: 'platzbuchungen', label: 'Belegungen', icon: <FontAwesomeIcon icon={faBorderAll} /> },
        {
            key: 'antraege',
            label: t('clubMember.preview.tabs.applications'),
            icon: <FontAwesomeIcon icon={faFileSignature} />,
            show: canViewApplications
        },
        { key: 'briefe', label: t('clubMember.preview.tabs.letters'), icon: <FontAwesomeIcon icon={faEnvelope} /> },
        { key: 'gutscheine', label: t('clubMember.preview.tabs.vouchers'), icon: <FontAwesomeIcon icon={faTag} /> },
        {
            key: 'nachrichten',
            label: t('clubMember.preview.tabs.messages'),
            icon: <FontAwesomeIcon icon={faComments} />
        },
        {
            key: 'dateien',
            label: t('clubMember.preview.tabs.files'),
            icon: <FontAwesomeIcon icon={faFolder} />,
            show: canViewFiles
        }
    ];

    const isActive = clubMember.is_active;
    const suspension = clubMember.suspension;
    // suspended members get their own colour so they read differently from
    // members that were hard deactivated or have exited
    const isSuspended = !isActive && suspension?.state === 'active';
    const initials = clubMember.name?.charAt(0)?.toUpperCase() ?? '?';
    const avatarUrl = clubMember.avatar ? `${process.env.NEXT_PUBLIC_API_HOST}image/${clubMember.avatar}` : null;

    return (
        <Loader isLoading={isLoading}>
            <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-1 tw-p-4">
                    {/* ── Header ── */}
                    <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-4">
                        {/* Avatar + identity */}
                        <div className="tw-flex tw-items-start tw-gap-6">
                            {/* Avatar circle with status dot */}
                            <div className="tw-relative tw-shrink-0">
                                <div className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-accents-2 tw-text-xl tw-font-bold tw-text-accents-6">
                                    {avatarUrl ? (
                                        <Image
                                            src={avatarUrl}
                                            alt={clubMember.name}
                                            width={56}
                                            height={56}
                                            className="tw-h-full tw-w-full tw-object-cover"
                                        />
                                    ) : (
                                        initials
                                    )}
                                </div>
                                <span
                                    className={`tw-border-background tw-absolute tw-bottom-0.5 tw-right-0.5 tw-h-3 tw-w-3 tw-rounded-full tw-border-2 ${
                                        isActive ? 'tw-bg-success' : isSuspended ? 'tw-bg-warning' : 'tw-bg-accents-3'
                                    }`}
                                />
                            </div>

                            {/* Name, badges, member number, chips */}
                            <div className="tw-flex tw-flex-col tw-gap-2">
                                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                    <span className="tw-text-lg tw-font-bold tw-text-accents-9">{clubMember.name}</span>
                                    <span className="tw-inline-flex tw-gap-2 tw-text-accents-8">
                                        {clubMember.departments?.map((d) => (
                                            <React.Fragment key={d.id}>
                                                <span>·</span>
                                                <span>{d.title}</span>
                                            </React.Fragment>
                                        ))}
                                    </span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    {clubMember.categories?.map((category) => (
                                        // the wrapper carries the trial indicator, since the badge itself
                                        // clips overflow through tw-truncate
                                        <div
                                            key={category.id}
                                            className="tw-relative"
                                            title={category.is_trial ? `${category.title} (auf Probe)` : undefined}
                                        >
                                            {category.is_trial ? (
                                                <span className="tw-absolute -tw-left-1 -tw-top-1 tw-z-10 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red-500 tw-ring-1 tw-ring-white" />
                                            ) : null}
                                            <div
                                                style={{ backgroundColor: category.color }}
                                                className="tw-cursor-auto tw-truncate tw-rounded-lg tw-px-2 tw-py-0.5 tw-text-xs tw-text-accents-0"
                                            >
                                                {category.title}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Info chips */}
                                <div className="tw-flex tw-flex-wrap tw-gap-2">
                                    {clubMember.entry_date && (
                                        <Badge color="secondary" flat>
                                            {t('clubMember.preview.badges.entry', {
                                                date: moment(clubMember.entry_date).format('l')
                                            })}
                                        </Badge>
                                    )}
                                    {clubMember.exit_date && (
                                        <Tooltip
                                            flat
                                            tooltip={[
                                                clubMember.cancellation_received_on &&
                                                    t('clubMember.exit.tooltip.receivedOn', {
                                                        date: moment(clubMember.cancellation_received_on).format('l')
                                                    }),
                                                clubMember.cancellation_reason &&
                                                    t('clubMember.exit.tooltip.reason', {
                                                        reason: clubMember.cancellation_reason
                                                    })
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        >
                                            <Badge color="warning" flat>
                                                {t('clubMember.preview.badges.exit', {
                                                    date: moment(clubMember.exit_date).format('l')
                                                })}
                                            </Badge>
                                        </Tooltip>
                                    )}
                                    {clubMember.lockers?.map((locker) => (
                                        <Badge key={locker.assignment_id} color="secondary" flat>
                                            {t('clubMember.preview.badges.locker', { number: locker.number })}
                                        </Badge>
                                    ))}
                                    {suspension && (
                                        <Badge color="warning" flat>
                                            {suspension.state === 'scheduled'
                                                ? t('clubMember.suspension.badge.scheduled', {
                                                      date: moment(suspension.starts_at).format('l')
                                                  })
                                                : suspension.ends_at
                                                  ? t('clubMember.suspension.badge.until', {
                                                        date: moment(suspension.ends_at).format('l')
                                                    })
                                                  : t('clubMember.suspension.badge.indefinite')}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Edit button + secondary actions */}
                        <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-gap-2">
                            <Badge color="secondary" className="tw-w-full tw-text-center" size="xl">
                                {clubMember.number}
                            </Badge>
                            <div className="tw-flex tw-gap-1">
                                <Tooltip flat tooltip={t('clubMember.preview.actions.call')}>
                                    <Button
                                        size="sm"
                                        flat
                                        color="secondary"
                                        href={`tel:${clubMember.phone}`}
                                        disabled={!clubMember.phone}
                                    >
                                        <FontAwesomeIcon icon={faPhone} />
                                    </Button>
                                </Tooltip>
                                <Tooltip flat tooltip={t('clubMember.preview.actions.letter')}>
                                    <Button size="sm" flat color="secondary" onPress={openLetterFormHandler}>
                                        <FontAwesomeIcon icon={faEnvelope} />
                                    </Button>
                                </Tooltip>
                                <Tooltip flat tooltip={t('clubMember.preview.actions.message')}>
                                    <Button size="sm" flat color="secondary" onPress={openMessageComponentHandler}>
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                    </Button>
                                </Tooltip>
                                <Tooltip flat tooltip={t('clubMember.preview.actions.printCard')}>
                                    <Button
                                        size="sm"
                                        flat
                                        color="secondary"
                                        onPress={handleDownloadMemberCardPrintTemplate}
                                    >
                                        <FontAwesomeIcon icon={faPrint} />
                                    </Button>
                                </Tooltip>
                                {!clubMember.hasAccount && (
                                    <Tooltip flat tooltip={t('clubMember.preview.actions.createAccount')}>
                                        <Button
                                            size="sm"
                                            flat
                                            color="secondary"
                                            disabled={!clubMember.email}
                                            onPress={openAccountFormHandler}
                                        >
                                            <FontAwesomeIcon icon={faUserPlus} />
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                            <Button
                                block
                                color="warning"
                                size="sm"
                                icon={<FontAwesomeIcon icon={faEdit} />}
                                onPress={openClubMemberFormHandler}
                            >
                                {t('clubMember.preview.actions.edit')}
                            </Button>
                            <div className="tw-flex tw-w-full tw-gap-1">
                                {isActive ? (
                                    <Button
                                        block
                                        flat
                                        color="error"
                                        size="sm"
                                        icon={<FontAwesomeIcon icon={faPause} />}
                                        onPress={openSuspendFormHandler}
                                    >
                                        {t('clubMember.preview.actions.suspend')}
                                    </Button>
                                ) : (
                                    <Button
                                        block
                                        flat
                                        color="success"
                                        size="sm"
                                        icon={<FontAwesomeIcon icon={faPlay} />}
                                        onPress={() => liftSuspensionHandler()}
                                    >
                                        {t('clubMember.preview.actions.liftSuspension')}
                                    </Button>
                                )}
                                {clubMember.exit_date ? (
                                    <Button
                                        block
                                        flat
                                        color="secondary"
                                        size="sm"
                                        icon={<FontAwesomeIcon icon={faRotateLeft} />}
                                        onPress={() => revertCancellationHandler()}
                                    >
                                        {t('clubMember.exit.actions.revertShort')}
                                    </Button>
                                ) : (
                                    <Button
                                        block
                                        flat
                                        color="error"
                                        size="sm"
                                        icon={<FontAwesomeIcon icon={faUserXmark} />}
                                        onPress={openCancelFormHandler}
                                    >
                                        {t('clubMember.exit.actions.cancelShort')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    <ServiceHoursComponent disabled member={clubMember} />
                </div>

                <InlineTabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

                {/* Viewport-aware height keeps a concrete size for tw-h-full DataTables while avoiding a
                    fixed 600px box that overflows the modal (double scrollbars) on shorter screens. */}
                <div className="tw-h-[clamp(20rem,60vh,40rem)] tw-overflow-y-auto">
                    {activeTab === 'rechnungsvorschau' && <ContributionsPreview clubMember={previewClubMember} />}

                    {activeTab === 'eigenschaften' && (
                        <ClubMemberPropertiesTab
                            clubMember={clubMember}
                            properties={properties}
                            remarks={clubMember.remarks}
                        />
                    )}

                    {activeTab === 'pferde' && showHorses && <MemberHorses memberId={resolvedClubMemberId} />}

                    {activeTab === 'rechnungen' && <ClubMemberOpenItems member={previewClubMember} />}

                    {activeTab === 'bookings' && <ClubMemberBookingsList member={previewClubMember} />}

                    {activeTab === 'platzbuchungen' && <ClubMemberAllocationsList member={previewClubMember} />}

                    {activeTab === 'antraege' && <ClubMemberApplications member={previewClubMember} />}

                    {activeTab === 'briefe' && <LetterList clubMember={previewClubMember} />}

                    {activeTab === 'gutscheine' && <ClubMemberVoucherList member={clubMember} />}

                    {activeTab === 'kategorie-historie' && (
                        <ClubMemberCategoryHistoryTimeline clubSlug={clubSlug} memberId={resolvedClubMemberId} />
                    )}

                    {activeTab === 'nachrichten' && <ClubMemberMessagesList member={previewClubMember} />}

                    {activeTab === 'geplante-aenderungen' && (
                        <div className="tw-relative">
                            {scheduledAssignments.map((assignment) => (
                                <div
                                    key={
                                        assignment.id ??
                                        `${assignment.new_category_id}-${assignment.scheduled_at}-${assignment.remove_selected ? 'remove' : 'add'}`
                                    }
                                    className="tw-relative tw-mb-2 tw-flex tw-items-center tw-gap-1 tw-border-0 tw-border-b tw-border-solid tw-border-b-accents-3 tw-pb-2 last:tw-mb-0 last:tw-border-b-0 last:tw-pb-0"
                                >
                                    <div className="tw-relative tw-flex tw-items-center tw-text-accents-5">
                                        <FontAwesomeIcon icon={assignment.remove_selected ? faTimes : faPlus} />
                                    </div>
                                    <div className="tw-flex-1 tw-pl-1">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <span
                                                className={`tw-text-xs tw-font-medium ${
                                                    assignment.remove_selected ? 'tw-text-error' : 'tw-text-success'
                                                }`}
                                            >
                                                {assignment.remove_selected
                                                    ? t('clubMember.preview.scheduled.remove')
                                                    : t('clubMember.preview.scheduled.assign')}
                                            </span>
                                            <span className="tw-text-xs tw-text-accents-7">
                                                {t('clubMember.preview.scheduled.at', {
                                                    date: formatScheduledAt(assignment.scheduled_at)
                                                })}
                                            </span>
                                        </div>
                                        <div className="tw-mt-2 tw-flex tw-flex-1 tw-items-center">
                                            <div
                                                style={{
                                                    backgroundColor: assignment.new_category?.color ?? '#000'
                                                }}
                                                className="tw-flex tw-h-5 tw-items-center tw-rounded-lg tw-px-1.5 tw-py-0.5 tw-text-[9px] tw-font-semibold tw-text-white"
                                            >
                                                <span>
                                                    {assignment.new_category?.title ??
                                                        t('clubMember.preview.scheduled.categoryFallback')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        className="tw-ml-auto"
                                        size="xs"
                                        flat
                                        color="error"
                                        onClick={() => removeScheduledCategoryAssignment(assignment.id)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === 'dateien' && canViewFiles && resolvedClubMemberId && (
                        <ClubMemberFileManager memberId={resolvedClubMemberId} />
                    )}
                </div>
            </div>
        </Loader>
    );
};
